import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import axios from 'axios';
import { Button } from '@jem-open/jem-ui';
import CreateMultiProductReconModal, {
  PayCycle,
} from './CreateMultiProductReconModal';

const localizer = momentLocalizer(moment);

interface CalendarInstance {
  id: string;
  summary: string;
  start: { date?: string; dateTime?: string };
  end: { date?: string; dateTime?: string };
  extendedProperties?: {
    private?: {
      reconScheduleId?: string;
      calendarEventType?: string;
    };
  };
}

interface MultiProductReconTabProps {
  employerId: string;
  employerName: string;
  payCycles: PayCycle[];
}

const initialRange = () => ({
  startTime: moment().startOf('month').format('YYYY-MM-DD'),
  endTime: moment()
    .startOf('month')
    .add(1, 'month')
    .add(7, 'days')
    .format('YYYY-MM-DD'),
});

const MultiProductReconTab = ({
  employerId,
  employerName,
  payCycles,
}: MultiProductReconTabProps) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [instances, setInstances] = useState<CalendarInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState(initialRange());

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get<CalendarInstance[]>(
        `/api/calendar_events/instances?employerId=${employerId}&startTime=${range.startTime}&endTime=${range.endTime}&calendarEventType=AUTOMATED_RECON_V2`,
      );
      setInstances(response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch recon schedule instances', error);
      setInstances([]);
    } finally {
      setLoading(false);
    }
  }, [employerId, range.startTime, range.endTime]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    fetchInstances();
  };

  const updateRange = (
    dateRanges: Date[] | { start: Date; end: Date },
  ) => {
    if (Array.isArray(dateRanges) && dateRanges.length > 0) {
      setRange({
        startTime: moment(dateRanges[0]).format('YYYY-MM-DD'),
        endTime: moment(dateRanges[dateRanges.length - 1]).format('YYYY-MM-DD'),
      });
      return;
    }
    if (
      dateRanges &&
      'start' in dateRanges &&
      'end' in dateRanges
    ) {
      setRange({
        startTime: moment(dateRanges.start).format('YYYY-MM-DD'),
        endTime: moment(dateRanges.end).format('YYYY-MM-DD'),
      });
    }
  };

  const events = instances
    .filter((i) => i.start?.date)
    .map((i) => {
      const day = moment(i.start.date as string).toDate();
      return {
        id: i.id,
        title: i.summary,
        allDay: true,
        start: day,
        end: day,
      };
    });

  return (
    <div className="px-4 pb-4 pt-2">
      <div className="mb-2 flex items-start justify-between gap-4">
        <div>
          <h2 className="mb-1 text-xl font-semibold">Multi-Product Recon</h2>
          <p className="text-sm text-muted-foreground">
            Schedule a recurring recon event that covers one or more products
            (EWA, Savings, JemMobile)
            <br />
            for the selected pay cycles.
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          Create Event
        </Button>
      </div>

      <Calendar
        views={['month', 'week', 'day']}
        min={moment('6:00am', 'h:mma').toDate()}
        max={moment('7:00pm', 'h:mma').toDate()}
        localizer={localizer}
        startAccessor="start"
        endAccessor="end"
        style={{ minHeight: '70vh', opacity: loading ? 0.5 : 1 }}
        events={events}
        onRangeChange={updateRange}
      />

      {isCreateModalOpen && (
        <CreateMultiProductReconModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseModal}
          employerName={employerName}
          employerId={employerId}
          payCycles={payCycles}
        />
      )}
    </div>
  );
};

export default MultiProductReconTab;
